// Extracted from web/components/WorkbenchDialogs.tsx; behavior and declaration bodies preserved.
import { useEffect, useState } from "react";

import { ArrowRight, Boxes, Search } from "lucide-react";

import type { Diagnostic, ModelDocument, Operation } from "../../../shared/contracts";

import { blockDependencyClosure, buildingBlocks, instantiateBlock } from "../../../core";

import { Button, Diagnostics, Field, Modal, Notice, errorText } from "../../components/Primitives";

export function BlocksDialog({
  document,
  onClose,
  onOperations,
}: {
  document: ModelDocument;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void;
}) {
  const [selected, setSelected] = useState(buildingBlocks[0]?.id || "");
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [industry, setIndustry] = useState("all");
  const industries = [...new Set(buildingBlocks.map(block => block.industry).filter(Boolean))].sort() as string[];
  const filteredBlocks = buildingBlocks.filter(block => (group === "all" || block.group === group) && (industry === "all" || block.industry === industry) && `${block.id} ${block.name} ${block.description} ${block.category} ${block.industry || ""} ${(block.tags || []).join(" ")}`.toLowerCase().includes(search.trim().toLowerCase()));
  const [prefix, setPrefix] = useState("");
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<any>();
  const [error, setError] = useState("");
  useEffect(() => {
    if (!filteredBlocks.some(block => block.id === selected)) {
      setSelected(filteredBlocks[0]?.id || ''); setPreview(undefined); setDecisions({}); setError('');
    }
  }, [group, industry, search, selected]);
  const block = buildingBlocks.find((b) => b.id === selected);
  const dependencies = selected
    ? blockDependencyClosure(selected).filter((id) => id !== selected)
    : [];
  const inspect = () => {
    try {
      setPreview(
        instantiateBlock(document, selected, {
          prefix,
          dependencies: Object.fromEntries(
            Object.entries(decisions).map(([id, action]) => [id, { action }]),
          ),
        } as any),
      );
      setError("");
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal
      title="Building blocks"
      subtitle={`${buildingBlocks.length} reusable reference models. Choose an industry, corporate function or shared foundation.`}
      wide
      onClose={onClose}
      footer={
        <>
          <span className="footer-note">
            Blocks create new identities; names alone never merge objects.
          </span>
          <Button onClick={onClose}>Cancel</Button>
          {preview ? (
            <Button
              variant="primary"
              disabled={
                !preview.operations?.length ||
                preview.diagnostics?.some(
                  (d: Diagnostic) => d.severity === "error",
                )
              }
              onClick={() => {
                onOperations(preview.operations, `Insert ${block?.name} block`);
                onClose();
              }}
            >
              Insert into draft
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={Search}
              disabled={!block || dependencies.some((d) => !decisions[d])}
              onClick={inspect}
            >
              Preview insertion
            </Button>
          )}
        </>
      }
    >
      <div className="form-row"><Field label="Block group"><select aria-label="Block group" value={group} onChange={e => {setGroup(e.target.value);if(e.target.value !== 'industry')setIndustry('all');}}><option value="all">All groups</option><option value="industry">Industry domains</option><option value="corporate">Corporate functions</option><option value="foundation">Shared foundations</option></select></Field>
        <Field label="Industry"><select aria-label="Industry" value={industry} disabled={group !== 'all' && group !== 'industry'} onChange={e => setIndustry(e.target.value)}><option value="all">All industries</option>{industries.map(name => <option key={name} value={name}>{name}</option>)}</select></Field></div>
      <div className="block-browser">
        <div className="block-list">
          <div className="search-input">
            <Search size={14} />
            <input
              placeholder="Find a building block"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <small className="subtle">{filteredBlocks.length} matching reference models</small>
          {!filteredBlocks.length && <p>No blocks match these filters.</p>}
          {filteredBlocks            .map((b) => (
              <button
                className={`block-choice ${selected === b.id ? "active" : ""}`}
                key={b.id}
                onClick={() => {
                  setSelected(b.id);
                  setPreview(undefined);
                  setDecisions({});
                }}
              >
                <Boxes size={18} />
                <span>
                  <strong>{b.name}</strong>
                  <small>
                    {b.industry || b.category} · {b.entityCount} entities
                  </small>
                </span>
                <ArrowRight size={14} />
              </button>
            ))}
        </div>
        <div className="block-detail">
          {block && (
            <>
              <div className="block-detail-icon">
                <Boxes size={30} />
              </div>
              <span className="eyebrow">REUSABLE MODEL · v{block.version}</span>
              <h3>{block.name}</h3>
              <p>{block.description}</p>
              {!!block.assumptions?.length && <details><summary>Scope and assumptions</summary><ul>{block.assumptions.map((text,index)=><li key={index}>{text}</li>)}</ul></details>}
              {!!block.sourceReferences?.length && <details><summary>Reference sources</summary><ul>{block.sourceReferences.map(reference=><li key={reference.url}><a href={reference.url} target="_blank" rel="noreferrer">{reference.title}</a></li>)}</ul><small>These are DataGlow reference models for review, not certified industry standards.</small></details>}
              <Field label="Optional name prefix">
                <input
                  placeholder="e.g. billing_"
                  value={prefix}
                  onChange={(e) => {
                    setPrefix(e.target.value);
                    setPreview(undefined);
                  }}
                />
              </Field>
              {dependencies.length > 0 && (
                <>
                  <div className="section-label">Dependency decisions</div>
                  {dependencies.map((dep) => (
                    <Field
                      key={dep}
                      label={
                        buildingBlocks.find((b) => b.id === dep)?.name || dep
                      }
                    >
                      <select
                        value={decisions[dep] || ""}
                        onChange={(e) => {
                          setDecisions({ ...decisions, [dep]: e.target.value });
                          setPreview(undefined);
                        }}
                      >
                        <option value="">Choose explicitly…</option>
                        <option value="create">
                          Create a separate dependency instance
                        </option>
                      </select>
                    </Field>
                  ))}
                  <p className="subtle">
                    Reuse requires an explicit existing-instance mapping. This
                    insertion creates the dependencies you select.
                  </p>
                </>
              )}
              {error && <Notice kind="error">{error}</Notice>}
              {preview && (
                <>
                  <Notice>
                    {preview.operations.length} canonical operations prepared.
                    Review diagnostics before insertion.
                  </Notice>
                  <Diagnostics items={preview.diagnostics || []} />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
